import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { VideoCamera, Plus, Trash, PencilSimple, Users, DownloadSimple, Clock, X, WifiMedium, SignOut, CheckCircle, XCircle, PlayCircle } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { LinkSimple, Copy } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

export const fmtDateTime = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};
const toLocalInput = (iso) => {
  const d = iso ? new Date(iso) : new Date(Date.now() + 3600 * 1000);
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};

const STATUS = {
  planifiee: { label: "Planifiée", cls: "bg-secondary text-secondary-foreground" },
  ouverte: { label: "Salle ouverte", cls: "bg-emerald-600 text-white" },
  terminee: { label: "Terminée", cls: "bg-muted text-muted-foreground" },
};

// Barre d'outils de Jitsi. Personne n'a l'invitation Jitsi : elle donnerait un lien direct vers la salle, hors du site,
// sans les restrictions ci-dessous. Les professeurs invitent avec le bouton « Inviter » du site (lien d'invitation).
// Les étudiants et invités n'ont ni tableau blanc, ni partage d'écran, ni outils de modération.
const ALL_BUTTONS = ["camera", "chat", "closedcaptions", "desktop", "download", "embedmeeting", "etherpad", "feedback",
  "filmstrip", "fullscreen", "hangup", "help", "highlight", "invite", "linktosalesforce", "livestreaming", "microphone",
  "mute-everyone", "mute-video-everyone", "noisesuppression", "participants-pane", "profile", "raisehand", "recording",
  "security", "select-background", "settings", "shareaudio", "sharedvideo", "shortcuts", "stats", "tileview",
  "toggle-camera", "videoquality", "whiteboard"];
const ALWAYS_HIDDEN = ["invite", "embedmeeting"]; // l'invitation passe par le lien du site (voir GuestLinks), jamais par Jitsi
const STUDENT_HIDDEN = [
  "whiteboard", "desktop", "shareaudio",            // invitation, tableau blanc, partage d'écran
  "profile", "sharedvideo", "etherpad",                                      // changement de nom, vidéo YouTube, notes partagées
  "recording", "livestreaming", "security", "mute-everyone", "mute-video-everyone", // outils de modération
  "feedback", "help", "download", "linktosalesforce",                        // bruit
];

function loadJitsiScript(appId) {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI && window.__jaasApp === appId) return resolve();
    const s = document.createElement("script");
    s.src = `https://8x8.vc/${appId}/external_api.js`;
    s.async = true;
    s.onload = () => { window.__jaasApp = appId; resolve(); };
    s.onerror = () => reject(new Error("Impossible de charger le service de visioconférence. Vérifiez votre connexion."));
    document.body.appendChild(s);
  });
}

/** Salle de visio plein écran (Jitsi / JaaS). */
export function LiveRoom({ session, lowBandwidth, onClose, guest }) {
  const holder = useRef(null);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [isMod, setIsMod] = useState(false);
  const [invOpen, setInvOpen] = useState(false);

  useEffect(() => {
    let jitsi = null;
    let ping = null;
    let attendanceId = null;
    let closed = false;
    const sendPresence = (event) =>
      attendanceId && api.post(guest ? `/public/live/${guest.token}/presence` : `/live-sessions/${session.id}/presence`, { attendance_id: attendanceId, event }).catch(() => {});
    const finish = () => {
      if (closed) return;
      closed = true;
      clearInterval(ping);
      sendPresence("leave");
      try { jitsi && jitsi.dispose(); } catch { /* ignore */ }
      onClose();
    };

    (async () => {
      try {
        const { data } = guest
          ? await api.post(`/public/live/${guest.token}/join`, { name: guest.name })
          : await api.post(`/live-sessions/${session.id}/join`);
        attendanceId = data.attendance_id;
        setIsMod(!!data.moderator && !guest);
        await loadJitsiScript(data.app_id);
        if (closed || !holder.current) return;
        jitsi = new window.JitsiMeetExternalAPI(data.domain, {
          roomName: `${data.app_id}/${data.room}`,
          jwt: data.jwt,
          parentNode: holder.current,
          width: "100%",
          height: "100%",
          lang: "fr",
          userInfo: { displayName: data.display_name },
          configOverwrite: {
            prejoinPageEnabled: true,
            disableDeepLinking: true,
            defaultLanguage: "fr",
            startWithAudioMuted: true,
            startWithVideoMuted: !!lowBandwidth,
            startAudioOnly: !!lowBandwidth,
            resolution: lowBandwidth ? 180 : 360,
            constraints: lowBandwidth ? { video: { height: { ideal: 180, max: 240 } } } : undefined,
            disableThirdPartyRequests: true,
            disableInviteFunctions: true,
            // Étudiants : le nom affiché vient du compte du site et ne peut pas être changé
            ...(data.moderator ? {} : {
              readOnlyName: true,
              disableProfile: true,
              prejoinConfig: { enabled: true, hideDisplayName: true },
            }),
            toolbarButtons: ALL_BUTTONS.filter((b) => !ALWAYS_HIDDEN.includes(b) && (data.moderator || !STUDENT_HIDDEN.includes(b))),
          },
          interfaceConfigOverwrite: { MOBILE_APP_PROMO: false, SHOW_JITSI_WATERMARK: false },
        });
        jitsi.addListener("videoConferenceJoined", () => setReady(true));
        jitsi.addListener("readyToClose", finish);
        jitsi.addListener("videoConferenceLeft", finish);
        ping = setInterval(() => sendPresence("ping"), 30000);
        setReady(true);
      } catch (e) {
        setError(e.response ? apiError(e) : e.message);
      }
    })();

    return () => {
      closed = true;
      clearInterval(ping);
      sendPresence("leave");
      try { jitsi && jitsi.dispose(); } catch { /* ignore */ }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black" data-testid="live-room">
      <div className="flex items-center justify-between gap-3 bg-neutral-900 px-4 py-2 text-white">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{session.title}</p>
          {session.course_title && <p className="truncate text-xs text-white/60">{session.course_title}</p>}
        </div>
        <div className="flex gap-2">
          {isMod && <Button size="sm" variant="secondary" onClick={() => setInvOpen((v) => !v)} data-testid="live-invite"><LinkSimple size={16} className="mr-1" /> Inviter</Button>}
          <Button size="sm" variant="secondary" onClick={onClose} data-testid="live-quit"><SignOut size={16} className="mr-1" /> Quitter</Button>
        </div>
      </div>
      {invOpen && (
        <div className="fixed right-3 top-14 z-[110] w-[min(92vw,26rem)] rounded-xl border border-border bg-card p-4 text-foreground shadow-xl" data-testid="live-invite-panel">
          <div className="mb-2 flex items-center justify-between">
            <p className="font-display text-base font-bold">Inviter des personnes extérieures</p>
            <button className="text-muted-foreground hover:text-foreground" onClick={() => setInvOpen(false)} aria-label="Fermer"><X size={16} /></button>
          </div>
          <GuestLinksBody session={session} />
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        <div ref={holder} className="absolute inset-0" />
        {!ready && !error && <div className="absolute inset-0 grid place-items-center text-sm text-white/70">Connexion à la salle…</div>}
        {error && (
          <div className="absolute inset-0 grid place-items-center p-6">
            <div className="max-w-md rounded-xl bg-card p-6 text-center">
              <p className="font-semibold text-destructive">Impossible d'entrer dans la salle</p>
              <p className="mt-2 text-sm text-muted-foreground">{error}</p>
              <Button className="mt-4" onClick={onClose}>Fermer</Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Petite fenêtre avant d'entrer : choix du mode économe. */
export function JoinDialog({ session, onCancel, onJoin }) {
  const [low, setLow] = useState(false);
  return (
    <Dialog open onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="join-dialog">
        <DialogHeader>
          <DialogTitle>Rejoindre la visio</DialogTitle>
          <DialogDescription>{session.title}</DialogDescription>
        </DialogHeader>
        <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3">
          <input type="checkbox" className="mt-1 h-4 w-4" checked={low} onChange={(e) => setLow(e.target.checked)} data-testid="low-bandwidth" />
          <span>
            <span className="flex items-center gap-1 text-sm font-semibold"><WifiMedium size={16} /> Connexion lente ? Mode économe</span>
            <span className="block text-xs text-muted-foreground">Caméra coupée et image réduite : consomme beaucoup moins de données. Vous entendez et voyez le cours normalement.</span>
          </span>
        </label>
        <p className="text-xs text-muted-foreground">Le navigateur vous demandera l'accès au micro et à la caméra. Utilisez Chrome, Edge, Firefox ou Safari récent.</p>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Annuler</Button>
          <Button className="bg-accent hover:bg-accent/90" onClick={() => onJoin(low)} data-testid="join-confirm"><VideoCamera size={16} className="mr-2" weight="fill" /> Entrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Hook : gère le bouton « Rejoindre » + la salle plein écran. */
export function useLiveJoin(onClosed) {
  const [asking, setAsking] = useState(null);
  const [room, setRoom] = useState(null);
  const node = (
    <>
      {asking && <JoinDialog session={asking} onCancel={() => setAsking(null)} onJoin={(low) => { setRoom({ session: asking, low }); setAsking(null); }} />}
      {room && <LiveRoom session={room.session} lowBandwidth={room.low} onClose={() => { setRoom(null); onClosed && onClosed(); }} />}
    </>
  );
  return { join: setAsking, node };
}

function SessionForm({ courseId, initial, onClose, onSaved }) {
  const [f, setF] = useState({
    title: initial?.title || "", description: initial?.description || "",
    start_at: toLocalInput(initial?.start_at), duration_min: initial?.duration_min || 90,
    replay_url: initial?.replay_url || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (f.title.trim().length < 2) return toast.error("Donnez un titre à la séance");
    setBusy(true);
    try {
      const body = { title: f.title, description: f.description, replay_url: f.replay_url.trim() || null, duration_min: Number(f.duration_min) || 90, start_at: new Date(f.start_at).toISOString() };
      if (initial) await api.put(`/live-sessions/${initial.id}`, body);
      else await api.post(`/courses/${courseId}/live-sessions`, body);
      toast.success(initial ? "Séance modifiée" : "Séance planifiée, les étudiants sont prévenus");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="live-form">
        <DialogHeader><DialogTitle>{initial ? "Modifier la séance" : "Planifier une séance en visio"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Titre</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Ex. Séance 3 : Analyse financière" data-testid="live-title" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div><Label>Date et heure</Label><Input type="datetime-local" value={f.start_at} onChange={(e) => setF({ ...f, start_at: e.target.value })} data-testid="live-start" /></div>
            <div><Label>Durée (minutes)</Label><Input type="number" min={10} max={480} value={f.duration_min} onChange={(e) => setF({ ...f, duration_min: e.target.value })} data-testid="live-duration" /></div>
          </div>
          <div><Label>Description (facultatif)</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} placeholder="Programme, documents à lire avant la séance…" /></div>
          {initial && (
            <div>
              <Label>Lien du replay (facultatif)</Label>
              <Input value={f.replay_url} onChange={(e) => setF({ ...f, replay_url: e.target.value })} placeholder="https://… (YouTube, Drive, Teams…)" data-testid="live-replay" />
              <p className="mt-1 text-xs text-muted-foreground">Après la séance, collez ici le lien de l'enregistrement : les étudiants sont prévenus et voient un bouton « Revoir le replay ».</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">La salle s'ouvre aux étudiants 15 minutes avant l'heure de début. Vous, vous pouvez entrer à tout moment.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={save} disabled={busy} data-testid="live-save">{busy ? "Enregistrement…" : "Enregistrer"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GuestLinksBody({ session }) {
  const [links, setLinks] = useState(null);
  const [label, setLabel] = useState("");
  const load = () => api.get(`/live-sessions/${session.id}/guest-links`).then((r) => setLinks(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, [session.id]);
  const urlOf = (l) => `${window.location.origin}/visio/invite/${l.token}`;
  const create = async () => {
    try { await api.post(`/live-sessions/${session.id}/guest-links`, { label }); setLabel(""); load(); toast.success("Lien créé"); } catch (e) { toast.error(apiError(e)); }
  };
  const copy = async (l) => {
    try { await navigator.clipboard.writeText(urlOf(l)); toast.success("Lien copié"); } catch { window.prompt("Copiez ce lien :", urlOf(l)); }
  };
  const revoke = async (l) => {
    if (!window.confirm("Désactiver ce lien ? Les personnes qui ne sont pas encore entrées ne pourront plus l'utiliser.")) return;
    try { await api.delete(`/live-sessions/${session.id}/guest-links/${l.id}`); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-3" data-testid="guest-body">
      <p className="text-sm text-muted-foreground">Chaque personne ouvre le lien, saisit son nom et entre dans la salle, sans compte. Elle a les mêmes restrictions qu'un étudiant. Le lien ne marche que pendant l'ouverture de la salle.</p>
      <div className="flex gap-2">
        <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Pour qui ? (facultatif)" maxLength={80} data-testid="guest-label" />
        <Button onClick={create} data-testid="guest-create"><Plus size={16} className="mr-1" /> Créer</Button>
      </div>
      <div className="max-h-[40vh] space-y-2 overflow-y-auto">
        {links && links.length === 0 && <p className="text-sm text-muted-foreground">Aucun lien pour cette séance.</p>}
        {(links || []).map((l) => (
          <div key={l.id} className="rounded-lg border border-border p-3" data-testid={`guest-link-${l.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold">{l.label || "Lien sans nom"} <span className="font-normal text-muted-foreground">· {l.uses} connexion(s)</span></p>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" onClick={() => copy(l)} data-testid={`guest-copy-${l.id}`}><Copy size={14} className="mr-1" /> Copier</Button>
                <Button size="sm" variant="outline" onClick={() => revoke(l)} title="Désactiver"><Trash size={14} /></Button>
              </div>
            </div>
            <p className="mt-1 break-all text-xs text-muted-foreground">{urlOf(l)}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function GuestLinks({ session, onClose }) {
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="guest-dialog">
        <DialogHeader>
          <DialogTitle>Inviter des personnes extérieures</DialogTitle>
          <DialogDescription>{session.title}</DialogDescription>
        </DialogHeader>
        <GuestLinksBody session={session} />
        <DialogFooter><Button onClick={onClose}>Fermer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Attendance({ session, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/live-sessions/${session.id}/attendance`).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))); }, [session.id]);
  const exportCsv = async () => {
    try {
      const res = await api.get(`/live-sessions/${session.id}/attendance.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `presence-${session.title.replace(/[^\w]+/g, "-")}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="attendance-dialog">
        <DialogHeader>
          <DialogTitle>Présence : {session.title}</DialogTitle>
          <DialogDescription>{data ? `${data.present_count} étudiant(s) présent(s) sur ${data.students_count}` : "Chargement…"}</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="max-h-[50vh] overflow-y-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase text-muted-foreground">
                <tr><th className="p-2">Nom</th><th className="p-2">Présence</th><th className="p-2">Durée</th></tr>
              </thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={3} className="p-4 text-center text-muted-foreground">Personne ne s'est encore connecté.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={r.user_id} className="border-t border-border">
                    <td className="p-2 font-medium">{r.name}{r.role === "invite" ? <Badge variant="outline" className="ml-2">Invité</Badge> : r.role !== "etudiant" && <Badge variant="secondary" className="ml-2">Enseignant</Badge>}</td>
                    <td className="p-2">{r.present
                      ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle size={16} weight="fill" /> Présent</span>
                      : <span className="inline-flex items-center gap-1 text-destructive"><XCircle size={16} weight="fill" /> Absent</span>}</td>
                    <td className="p-2 text-muted-foreground">{r.present ? `${Math.max(1, Math.round(r.duration_s / 60))} min` : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={exportCsv} data-testid="attendance-csv"><DownloadSimple size={16} className="mr-1" /> Exporter (Excel)</Button>
          <Button onClick={onClose}>Fermer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function SessionCard({ s, canEdit, host, onJoin, onEdit, onDelete, onAttendance, onGuests, showCourse }) {
  const st = STATUS[s.status] || STATUS.planifiee;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center" data-testid={`live-${s.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="font-display text-base font-bold leading-tight">{s.title}</h4>
          <Badge className={st.cls}>{st.label}</Badge>
        </div>
        <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground"><Clock size={14} /> <span className="capitalize">{fmtDateTime(s.start_at)}</span> · {s.duration_min} min</p>
        {showCourse && s.course_title && <p className="text-xs text-muted-foreground">{s.course_title}</p>}
        {s.description && <p className="mt-1 text-sm text-muted-foreground">{s.description}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {(s.status === "ouverte" || ((canEdit || host) && s.status !== "terminee")) && (
          <Button className="bg-accent hover:bg-accent/90" onClick={() => onJoin(s)} data-testid={`join-live-${s.id}`}>
            <VideoCamera size={16} className="mr-2" weight="fill" /> {canEdit || host ? "Lancer / rejoindre" : "Rejoindre"}
          </Button>
        )}
        {s.replay_url && (
          <a href={s.replay_url} target="_blank" rel="noreferrer noopener">
            <Button variant="outline" data-testid={`replay-${s.id}`}><PlayCircle size={16} className="mr-2" weight="fill" /> Revoir le replay</Button>
          </a>
        )}
        {canEdit && onGuests && s.status !== "terminee" && <Button variant="outline" size="icon" onClick={() => onGuests(s)} title="Inviter des personnes extérieures" data-testid={`guests-${s.id}`}><LinkSimple size={16} /></Button>}
        {canEdit && <Button variant="outline" size="icon" onClick={() => onAttendance(s)} title="Présence" data-testid={`attendance-${s.id}`}><Users size={16} /></Button>}
        {canEdit && <Button variant="outline" size="icon" onClick={() => onEdit(s)} title="Modifier"><PencilSimple size={16} /></Button>}
        {canEdit && <Button variant="outline" size="icon" onClick={() => onDelete(s)} title="Supprimer" data-testid={`delete-live-${s.id}`}><Trash size={16} /></Button>}
      </div>
    </div>
  );
}

/** Onglet « Visio » d'un cours. */
export function LiveSessionsPanel({ courseId, canEdit }) {
  const [list, setList] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null); // {} = nouveau, objet = édition
  const [att, setAtt] = useState(null);
  const [guests, setGuests] = useState(null);
  const load = () => api.get(`/courses/${courseId}/live-sessions`).then((r) => setList(r.data));
  const live = useLiveJoin(load);
  useEffect(() => { load(); api.get("/live-config").then((r) => setCfg(r.data)); }, [courseId]);

  const remove = async (s) => {
    if (!window.confirm(`Supprimer la séance « ${s.title} » ?`)) return;
    try { await api.delete(`/live-sessions/${s.id}`); toast.success("Séance supprimée"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  const upcoming = (list || []).filter((s) => s.status !== "terminee").sort((a, b) => a.start_at.localeCompare(b.start_at));
  const past = (list || []).filter((s) => s.status === "terminee");

  return (
    <div className="space-y-5" data-testid="live-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-xl font-bold">Cours en visioconférence</h3>
          <p className="text-sm text-muted-foreground">La salle s'ouvre 15 minutes avant le début. Un rappel est envoyé la veille et 15 minutes avant.</p>
        </div>
        {canEdit && <Button onClick={() => setForm({})} data-testid="live-new"><Plus size={16} className="mr-1" /> Planifier une séance</Button>}
      </div>
      {cfg && !cfg.enabled && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
          La visioconférence n'est pas encore activée sur ce site (clés JaaS manquantes). Vous pouvez planifier les séances, mais personne ne pourra entrer tant que l'administrateur n'a pas fini la configuration.
        </div>
      )}
      {list && upcoming.length === 0 && <p className="text-sm text-muted-foreground">Aucune séance à venir.</p>}
      <div className="space-y-3">
        {upcoming.map((s) => <SessionCard key={s.id} s={s} canEdit={canEdit} onJoin={live.join} onEdit={setForm} onDelete={remove} onAttendance={setAtt} onGuests={setGuests} />)}
      </div>
      {past.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">Séances passées</h4>
          {past.map((s) => <SessionCard key={s.id} s={s} canEdit={canEdit} onJoin={live.join} onEdit={setForm} onDelete={remove} onAttendance={setAtt} onGuests={setGuests} />)}
        </div>
      )}
      {form && <SessionForm courseId={courseId} initial={form.id ? form : null} onClose={() => setForm(null)} onSaved={() => { setForm(null); load(); }} />}
      {att && <Attendance session={att} onClose={() => setAtt(null)} />}
      {guests && <GuestLinks session={guests} onClose={() => setGuests(null)} />}
      {live.node}
    </div>
  );
}
